import { ListSection } from '@coast/schemas';
import { merge } from 'lodash';
import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useCardSelection } from '@shared/api/providers/cardSelection';
import { MBView } from '@shared/components/MBView';
import { CardLayoutConfigProvider } from '@shared/components/blocks/contexts/layoutConfig';
import { CardThemeContext } from '@shared/components/blocks/contexts/theme';
import { MBHover } from '@shared/components/hover';
import { MBSelectableDot } from '@shared/components/list/row/selectable';
import { useUpgradeModal } from '@shared/scenes/billing/upgradeModal/provider';
import { useHasCardOptionsMenu } from '@shared/scenes/cards/details/useHasCardOptionsMenu';
import {
  ColorTheme,
  ColorThemeContext,
  PRICING_LIMIT_HISTORY_DAYS,
  defaultTheme,
  grid,
} from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { MBTouchableHighlight } from '../touchable/highlight';
import { MBCardMenu } from './menu';
import { cardThemes } from './themes';

export interface MBCardBaseProps {
  card: MBAPI.Card;
  cardTheme?: ListSection['cardTheme'];
  children: ReactNode;
  onPress?(card: MBAPI.Card): void;
  rounded?: boolean;
}

export function MBCardBase({
  children,
  card,
  cardTheme,
  onPress,
  rounded = true,
}: MBCardBaseProps) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const { show: showUpgradeModal } = useUpgradeModal();
  const [cardIsHovered, setCardIsHovered] = useState(false);

  const hasCardOptionsMenu = useHasCardOptionsMenu();

  const { isCardSelected, toggleSelectedCard, selectionActive } = useCardSelection(card.id);

  const colorTheme = useMemo<ColorTheme>(() => {
    const chosenTheme = card.planLimitImposed ? cardThemes.limitImposed : cardThemes.default;
    return merge({}, defaultTheme, chosenTheme);
  }, [card]);

  const onPressCard = useCallback(() => {
    setCardIsHovered(false);
    if (card.planLimitImposed) {
      showUpgradeModal({
        title: i18n('card.upgradeModal.title', { count: PRICING_LIMIT_HISTORY_DAYS }),
        description: i18n('card.upgradeModal.description'),
      });
    } else if (selectionActive) {
      toggleSelectedCard();
    } else {
      onPress?.(card);
    }
  }, [setCardIsHovered, card, selectionActive, showUpgradeModal, toggleSelectedCard, onPress]);

  const displayOptions = !card.planLimitImposed && !selectionActive && hasCardOptionsMenu;

  const isCardSelectable = !card.planLimitImposed && selectionActive;

  return (
    <MBHover onHoverStart={() => setCardIsHovered(true)} onHoverEnd={() => setCardIsHovered(false)}>
      <MBView
        style={[
          styles.container,
          { backgroundColor: colorTheme.background },
          !!rounded && styles.rounded,
          isCardSelected && { borderColor: colors.accent, borderWidth: 1 },
        ]}
        row
      >
        <MBView flex style={[styles.content, !!rounded && styles.rounded]}>
          <ColorThemeContext.Provider value={colorTheme}>
            <MBTouchableHighlight onPress={onPressCard} style={styles.touchable}>
              <CardThemeContext.Provider value={cardTheme}>
                <CardLayoutConfigProvider>{children}</CardLayoutConfigProvider>
              </CardThemeContext.Provider>
              {displayOptions && (
                <MBCardMenu
                  card={card}
                  menuIsVisible={!isDesktop || cardIsHovered}
                  onMenuClose={() => setCardIsHovered(false)}
                />
              )}
              {isCardSelectable && (
                <MBView style={styles.selectDot}>
                  <MBSelectableDot selected={isCardSelected} size={grid(4)} />
                </MBView>
              )}
            </MBTouchableHighlight>
          </ColorThemeContext.Provider>
        </MBView>
      </MBView>
    </MBHover>
  );
}

export const styles = StyleSheet.create({
  container: {
    shadowColor: 'rgba(0, 0, 0, 0.2)',
    shadowOffset: { width: 2, height: 2 },
    shadowRadius: 4,
    shadowOpacity: 1,
    elevation: 3,
  },
  content: {
    overflow: 'hidden',
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
  },
  touchable: {
    flex: 1,
    position: 'relative',
  },
  rounded: {
    borderRadius: grid(1),
  },
  selectDot: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
});
